import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useServerFn } from '@tanstack/react-start';
import { AccountShell } from '@/components/customer/AccountShell';
import { listMyTiffin } from '@/lib/tiffin/tiffin.functions';
import { formatPaise } from '@/lib/format/money';

const title = 'My Tiffin Orders & Subscriptions | Poonam Kapur Healthy Kitchen';
const description = 'View your tiffin orders, delivery schedule and payment status.';
export const Route = createFileRoute('/_customer/account/tiffin')({
  head: () => ({ meta: [{ title }, { name: 'description', content: description }, { property: 'og:title', content: title }, { property: 'og:description', content: description }, { property: 'og:type', content: 'website' }, { name: 'twitter:card', content: 'summary' }] }),
  component: MyTiffin,
});

const statusText = (status: string, method: string | null) =>
  status === 'active' ? 'Active · Paid'
  : status === 'cancelled' ? 'Cancelled'
  : status === 'paused' ? 'Paused'
  : method === 'cod' ? 'Pending · Cash on delivery (unpaid)' : 'Pending payment';

function MyTiffin() {
  const fetch = useServerFn(listMyTiffin);
  const query = useQuery({ queryKey: ['tiffin', 'mine'], queryFn: () => fetch() });
  return (
    <AccountShell title="Tiffin orders & subscriptions">
      {query.isPending ? <p role="status">Loading tiffin orders…</p>
        : query.isError ? <p role="alert">Your tiffin orders could not be loaded.</p>
        : !query.data?.length ? <div className="space-y-4"><p>No tiffin orders yet.</p><Link to="/tiffin" className="text-primary underline">View tiffin options</Link></div>
        : <div className="divide-y divide-border">{query.data.map((s) => {
            const p = (s.price_snapshot ?? {}) as Record<string, number | string | null>;
            const a = (s.address_snapshot ?? {}) as Record<string, string | null>;
            const dates = Array.isArray(s.schedule_snapshot) ? (s.schedule_snapshot as string[]) : [];
            return (
              <section key={s.id} className="py-5 space-y-2 text-sm">
                <p className="font-semibold">{s.order_ref ?? 'Reference pending'}</p>
                <h2 className="text-base font-semibold">{s.meal_selection === 'both' ? 'Lunch + Dinner' : s.meal_selection === 'lunch' ? 'Lunch only' : 'Dinner only'} · {s.duration_label ?? `${s.service_days} delivery days`}</h2>
                <p>{statusText(s.status, s.payment_method)} · {s.payment_method === 'cod' ? 'Cash on delivery' : s.payment_method === 'razorpay' ? 'Online (Razorpay)' : '—'}</p>
                <p>Starts {s.start_date ?? dates[0] ?? '—'} · {s.service_days} delivery days · {a.area ?? ''}</p>
                <dl className="grid grid-cols-2 gap-1 max-w-sm">
                  <dt>Food</dt><dd className="text-right">{formatPaise(Number(p.food_paise ?? 0))}</dd>
                  <dt>Delivery</dt><dd className="text-right">{formatPaise(Number(p.delivery_paise ?? 0))}</dd>
                  <dt>Discount</dt><dd className="text-right">−{formatPaise(Number(p.discount_paise ?? 0))}</dd>
                  <dt>Tax{p.gst_mode === 'inclusive' ? ' (included)' : ''}</dt><dd className="text-right">{formatPaise(Number(p.tax_paise ?? 0))}</dd>
                  <dt className="font-semibold">Total</dt><dd className="text-right font-semibold">{formatPaise(s.total_paise)}</dd>
                </dl>
                {dates.length > 0 && <details><summary className="cursor-pointer text-primary">Delivery dates</summary><p className="mt-2 break-words">{dates.join(', ')}</p></details>}
              </section>
            );
          })}</div>}
    </AccountShell>
  );
}
